import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../components/Toast'

interface ConsulateCommunity {
  id: string
  name: string
  display_name: string | null
  api_key_prefix: string
  status: string
  last_connected_at: string | null
  rate_limit_per_hour: number
}

function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1] || ''
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export default function SettingsPage() {
  const { user, logout } = useAuth()
  const toast = useToast()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null)

  // ─── Consulate admin state (task-029 Phase B) ───
  const [consulateCommunities, setConsulateCommunities] = useState<ConsulateCommunity[]>([])
  const [consulateLoaded, setConsulateLoaded] = useState(false)
  const [confirmRotateId, setConfirmRotateId] = useState<string | null>(null)

  // ─── D8 pair-token state (Apr 15) ───
  const [pairingForId, setPairingForId] = useState<string | null>(null)
  const [pairToken, setPairToken] = useState<{ token: string; expires_at: string; community_name: string } | null>(null)
  const [pairDeviceLabel, setPairDeviceLabel] = useState('')
  const [pairCopied, setPairCopied] = useState(false)
  const [needsKeyRotation, setNeedsKeyRotation] = useState<string | null>(null)
  const [rotatingId, setRotatingId] = useState<string | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/consulate/my-admin-communities', { headers: getAuthHeader() })
        if (res.ok) {
          const data = await res.json()
          setConsulateCommunities(data.communities || [])
        }
      } catch {
        /* no-op — user has no admin scope, empty list is correct */
      } finally {
        setConsulateLoaded(true)
      }
    })()
  }, [])

  async function handleRotateAndDownload(communityId: string) {
    setRotatingId(communityId)
    setConfirmRotateId(null)
    try {
      const res = await fetch(`/api/consulate/rotate-bundle/${communityId}`, {
        method: 'POST',
        headers: getAuthHeader(),
      })
      if (!res.ok) {
        const body = await res.text()
        let detail = body.slice(0, 200)
        try { const j = JSON.parse(body); if (j.error) detail = j.error } catch {}
        toast(`Bundle rotation failed: ${detail}`, 'error')
        return
      }
      // Pull headers BEFORE blob (stream closes after blob read)
      const newPrefix = res.headers.get('X-Consulate-New-Key-Prefix') || 'unknown'
      const disposition = res.headers.get('Content-Disposition') || ''
      const filenameMatch = disposition.match(/filename="([^"]+)"/)
      const filename = filenameMatch ? filenameMatch[1] : `consulate-${communityId}.tar.gz`
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast(`New key issued: ${newPrefix}… Bundle download started. Old key is now revoked.`, 'success')
      // Refresh local list to show new prefix
      const refresh = await fetch('/api/consulate/my-admin-communities', { headers: getAuthHeader() })
      if (refresh.ok) {
        const data = await refresh.json()
        setConsulateCommunities(data.communities || [])
      }
    } catch (e) {
      toast(`Network error during rotation: ${e instanceof Error ? e.message : 'unknown'}`, 'error')
    } finally {
      setRotatingId(null)
    }
  }

  // ─── D8: pair-token issuance for Consulate desktop app ───
  async function handleIssuePairToken(communityId: string) {
    setPairingForId(communityId)
    setPairCopied(false)
    setNeedsKeyRotation(null)
    setPairToken(null)
    try {
      const res = await fetch(`/api/consulate/communities/${communityId}/pair-token`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_label: pairDeviceLabel || null,
          ttl_seconds: 1800, // 30 min default
        }),
      })
      if (res.status === 412) {
        // No encrypted admin_key on file yet — prompt to rotate first.
        setNeedsKeyRotation(communityId)
        return
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        toast(`Pair token failed: ${err.error || `HTTP ${res.status}`}`, 'error')
        return
      }
      const data = await res.json()
      setPairToken({ token: data.token, expires_at: data.expires_at, community_name: data.community_name })
    } catch (e) {
      toast(`Pair token failed: ${String(e)}`, 'error')
    }
  }

  async function handleRotateAdminKey(communityId: string) {
    try {
      const res = await fetch(`/api/consulate/communities/${communityId}/rotate-admin-key`, {
        method: 'POST',
        headers: getAuthHeader(),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        toast(`Key rotation failed: ${err.error || `HTTP ${res.status}`}`, 'error')
        return
      }
      toast('Admin key rotated — you can now generate a pairing token.', 'success')
      setNeedsKeyRotation(null)
      // Auto-retry pair token generation now that encrypted key exists.
      await handleIssuePairToken(communityId)
    } catch (e) {
      toast(`Key rotation failed: ${String(e)}`, 'error')
    }
  }

  function copyPairToken() {
    if (!pairToken) return
    navigator.clipboard.writeText(pairToken.token).then(() => {
      setPairCopied(true)
      setTimeout(() => setPairCopied(false), 2000)
    })
  }

  async function handlePasswordChange() {
    if (newPassword !== confirmPassword) {
      setMessage({ text: 'Passwords do not match', type: 'error' })
      return
    }
    if (newPassword.length < 8) {
      setMessage({ text: 'Password must be at least 8 characters', type: 'error' })
      return
    }
    try {
      const res = await fetch('/api/settings/password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]) || ''}`,
        },
        body: JSON.stringify({ password: newPassword }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        setMessage({ text: 'Password updated', type: 'success' })
        setNewPassword('')
        setConfirmPassword('')
      } else {
        setMessage({ text: data.error || 'Failed to update password', type: 'error' })
      }
    } catch {
      setMessage({ text: 'Network error', type: 'error' })
    }
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <h1 className="text-lg font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>Settings</h1>

      {/* Profile */}
      <section className="p-4 rounded-lg mb-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <h2 className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>Profile</h2>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <span style={{ color: 'var(--text-muted)' }}>Name</span>
          <span style={{ color: 'var(--text-primary)' }}>{user?.userName}</span>
          <span style={{ color: 'var(--text-muted)' }}>Type</span>
          <span style={{ color: 'var(--text-primary)' }} className="capitalize">{user?.userType}</span>
          <span style={{ color: 'var(--text-muted)' }}>Community</span>
          <span style={{ color: 'var(--text-primary)' }}>{user?.communityName || 'N/A'}</span>
          <span style={{ color: 'var(--text-muted)' }}>Role</span>
          <span style={{ color: 'var(--text-primary)' }} className="capitalize">{user?.isOperator ? 'Operator' : 'Member'}</span>
        </div>
      </section>

      {/* Change password */}
      <section className="p-4 rounded-lg mb-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <h2 className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>Change Password</h2>
        <div className="flex flex-col gap-2">
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            className="px-3 py-2 rounded text-sm outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          <input
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            className="px-3 py-2 rounded text-sm outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          {message && (
            <p className="text-xs" style={{ color: message.type === 'error' ? 'var(--danger)' : 'var(--success)' }}>
              {message.text}
            </p>
          )}
          <button
            onClick={handlePasswordChange}
            disabled={!newPassword || !confirmPassword}
            className="text-xs px-3 py-1.5 rounded-md self-start disabled:opacity-50"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            Update Password
          </button>
        </div>
      </section>

      {/* Consulate admin (task-029 Phase B) — only shown if user admins at least one external community */}
      {consulateLoaded && consulateCommunities.length > 0 && (
        <section className="p-4 rounded-lg mb-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
          <h2 className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>External Communities (Consulate)</h2>
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            Communities you administer. Rotate the key + download a fresh bundle for your local consulate-client.
          </p>
          {consulateCommunities.map(c => (
            <div key={c.id} className="mb-3 p-3 rounded" style={{ background: 'var(--bg-body)', border: '1px solid var(--border)' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {c.display_name || c.name}
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    id: <code>{c.id}</code>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                    <span style={{ color: 'var(--text-muted)' }}>Current key prefix</span>
                    <code style={{ color: 'var(--text-primary)' }}>{c.api_key_prefix}…</code>
                    <span style={{ color: 'var(--text-muted)' }}>Status</span>
                    <span style={{ color: c.status === 'active' ? 'var(--success)' : 'var(--danger)' }} className="capitalize">{c.status}</span>
                    <span style={{ color: 'var(--text-muted)' }}>Last connected</span>
                    <span style={{ color: 'var(--text-primary)' }}>{c.last_connected_at ? new Date(c.last_connected_at).toLocaleString() : 'Never'}</span>
                    <span style={{ color: 'var(--text-muted)' }}>Rate limit</span>
                    <span style={{ color: 'var(--text-primary)' }}>{c.rate_limit_per_hour}/hr</span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleIssuePairToken(c.id)}
                  disabled={pairingForId === c.id && !pairToken && !needsKeyRotation}
                  className="text-xs px-3 py-1.5 rounded font-medium disabled:opacity-50"
                  style={{ background: 'var(--accent)', color: '#fff' }}
                >
                  📱 Pair Desktop App
                </button>
                <button
                  onClick={() => setConfirmRotateId(c.id)}
                  disabled={rotatingId !== null}
                  className="text-xs px-3 py-1.5 rounded font-medium disabled:opacity-50"
                  style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', cursor: rotatingId !== null ? 'wait' : 'pointer' }}
                >
                  {rotatingId === c.id ? '⏳ Rotating…' : '🔄 Legacy bundle'}
                </button>
              </div>

              {/* D8 Pair flow UI — inline panel under the buttons */}
              {pairingForId === c.id && needsKeyRotation === c.id && (
                <div className="mt-3 p-3 rounded" style={{ background: 'rgba(214,150,29,0.08)', border: '1px solid var(--warning, #d6961d)' }}>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-primary)' }}>
                    <strong>One-time setup:</strong> this community's admin key isn't stored encrypted yet. Rotate it once to enable pairing. The new key replaces the current one.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => handleRotateAdminKey(c.id)}
                      className="text-xs px-3 py-1.5 rounded font-medium"
                      style={{ background: 'var(--warning, #d6961d)', color: '#fff' }}>
                      Rotate now & continue
                    </button>
                    <button onClick={() => { setPairingForId(null); setNeedsKeyRotation(null); }}
                      className="text-xs px-3 py-1.5 rounded"
                      style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {pairingForId === c.id && pairToken && (
                <div className="mt-3 p-3 rounded" style={{ background: 'var(--bg-body)', border: '1px solid var(--accent)' }}>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-primary)' }}>
                    <strong>Pairing token for {pairToken.community_name}:</strong>
                  </p>
                  <code className="block text-xs p-2 rounded mb-2 font-mono break-all" style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
                    {pairToken.token}
                  </code>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <button onClick={copyPairToken}
                      className="text-xs px-3 py-1.5 rounded font-medium"
                      style={{ background: 'var(--accent)', color: '#fff' }}>
                      {pairCopied ? '✓ Copied' : '📋 Copy token'}
                    </button>
                    <button onClick={() => { setPairingForId(null); setPairToken(null); }}
                      className="text-xs px-3 py-1.5 rounded"
                      style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
                      Done
                    </button>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Single-use. Expires {new Date(pairToken.expires_at).toLocaleString()}. Send via secure channel (Signal, encrypted email). Recipient pastes into Consulate.app on first launch.
                  </p>
                  <div className="mt-2">
                    <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Device label (optional, audit only):</label>
                    <input
                      value={pairDeviceLabel}
                      onChange={e => setPairDeviceLabel(e.target.value)}
                      placeholder="e.g. Michael laptop"
                      className="block text-xs px-2 py-1 rounded w-full mt-1"
                      style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    />
                  </div>
                </div>
              )}
              {confirmRotateId === c.id && (
                <div className="mt-3 p-3 rounded" style={{ background: 'rgba(248,81,73,0.08)', border: '1px solid var(--danger)' }}>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-primary)' }}>
                    <strong>Re-issuing will invalidate your current client's connection.</strong> Any consulate-client running against the old key will start receiving 401s on the next heartbeat. You'll need to deploy this new bundle before your bridge reconnects.
                  </p>
                  <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                    Continue only if you're ready to replace the client config now.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRotateAndDownload(c.id)}
                      className="text-xs px-3 py-1.5 rounded font-medium"
                      style={{ background: 'var(--danger)', color: '#fff' }}
                    >
                      Yes, rotate and download
                    </button>
                    <button
                      onClick={() => setConfirmRotateId(null)}
                      className="text-xs px-3 py-1.5 rounded"
                      style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Sign out */}
      <button
        onClick={logout}
        className="text-xs px-4 py-2 rounded-md"
        style={{ background: 'var(--bg-surface)', color: 'var(--danger)', border: '1px solid var(--border)' }}
      >
        Sign Out
      </button>
    </div>
  )
}
